import React, { useState, useEffect, useMemo, useCallback } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../../App";
import { downloadRowsAsXlsx } from "../../utils/excelDownload";

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
  .ma-kpi  { display:grid; grid-template-columns:repeat(auto-fit,minmax(145px,1fr)); gap:12px; }
  .ma-2col { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  .ma-3col { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; }
  .ma-frow { display:flex; gap:12px; flex-wrap:wrap; align-items:flex-end; }
  .ma-dark-sel { background:#1a1b2e !important; color:#f9fafb !important; color-scheme:dark; }
  .ma-dark-sel option { background:#1a1b2e !important; color:#f9fafb !important; }
  input[type="date"].ma-dark-sel { color-scheme:dark; }
  @media(max-width:900px){
    .ma-3col { grid-template-columns:1fr 1fr !important; }
  }
  @media(max-width:680px){
    .ma-2col { grid-template-columns:1fr !important; }
    .ma-3col { grid-template-columns:1fr !important; }
    .ma-kpi  { grid-template-columns:repeat(2,1fr) !important; }
    .ma-frow { gap:8px !important; }
  }
`;

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS_LABEL = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_FULL  = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const CATEGORY_ICONS   = { Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽", Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌" };
const CATEGORY_COLORS  = { Travel:"#818cf8", Hotel:"#22d3ee", Food:"#34d399", Fuel:"#fbbf24", Courier:"#a78bfa", Stationeries:"#f87171", Prints:"#f472b6", Miscellaneous:"#94a3b8" };
const CAT_COLOR_LIST   = ["#818cf8","#22d3ee","#34d399","#fbbf24","#a78bfa","#f87171","#f472b6","#94a3b8"];
const FOOD_SUB_COLORS  = { BF:"#22d3ee", Lunch:"#34d399", Dinner:"#a78bfa" };
const FOOD_SUB_ICONS   = { BF:"☕", Lunch:"🥗", Dinner:"🍽️" };
const FOOD_SUB_ORDER   = ["BF","Lunch","Dinner"];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(n)      { return parseFloat(n||0).toLocaleString("en-IN", { minimumFractionDigits:2 }); }
function fmtShort(n) {
  const v = parseFloat(n||0);
  if (v >= 100000) return `₹${(v/100000).toFixed(1)}L`;
  if (v >= 1000)   return `₹${(v/1000).toFixed(1)}K`;
  return `₹${Math.round(v)}`;
}

function getRange(year, month, customFrom, customTo) {
  if (year === "custom") return { from: customFrom, to: customTo };
  if (year === "last12") {
    const today = new Date();
    const from  = new Date(today); from.setMonth(from.getMonth()-12); from.setDate(1);
    return { from: from.toISOString().substring(0,10), to: today.toISOString().substring(0,10) };
  }
  if (month) {
    const m  = month.padStart(2,"0");
    const dy = new Date(parseInt(year), parseInt(month), 0).getDate();
    return { from:`${year}-${m}-01`, to:`${year}-${m}-${String(dy).padStart(2,"0")}` };
  }
  const today = new Date().toISOString().substring(0,10);
  return { from:`${year}-01-01`, to: year===String(currentYear) ? today : `${year}-12-31` };
}

function periodLabel(year, month, customFrom, customTo) {
  if (year === "custom") return customFrom && customTo ? `${new Date(customFrom).toLocaleDateString("en-IN")} – ${new Date(customTo).toLocaleDateString("en-IN")}` : "Custom Range";
  if (year === "last12") return "Last 12 Months";
  if (month) return `${MONTHS_FULL[parseInt(month)-1]} ${year}`;
  return `${year} (YTD)`;
}

// ── Aggregation ───────────────────────────────────────────────────────────────
function aggregateRows(rows) {
  const cats = {}, emps = {}, trips = {}, byMonth = {}, depts = {}, foodSub = {};
  let totalSpent = 0, cashAmt = 0, cashCnt = 0, billCnt = 0;

  rows.forEach(r => {
    const amt = parseFloat(r.amount||0);
    totalSpent += amt;
    cats[r.category] = (cats[r.category]||0) + amt;
    if (r.category === "Food" && r.food_subcategory)
      foodSub[r.food_subcategory] = (foodSub[r.food_subcategory]||0) + amt;

    if (r.bill_type==="cash_voucher"||r.bill_type==="cash_bill") { cashAmt += amt; cashCnt++; }
    else billCnt++;

    const ek = r.employee_email || r.employee_name;
    if (!emps[ek]) emps[ek] = { name:r.employee_name, dept:r.department||"", total:0, categories:{}, foodSub:{}, trips:new Set(), cash:0 };
    emps[ek].total += amt;
    emps[ek].categories[r.category] = (emps[ek].categories[r.category]||0) + amt;
    if (r.category==="Food"&&r.food_subcategory)
      emps[ek].foodSub[r.food_subcategory] = (emps[ek].foodSub[r.food_subcategory]||0) + amt;
    emps[ek].trips.add(r.trip_title);
    if (r.bill_type==="cash_voucher"||r.bill_type==="cash_bill") emps[ek].cash += amt;

    const dept = r.department||"Other";
    if (!depts[dept]) depts[dept] = { name:dept, total:0, employees:new Set() };
    depts[dept].total += amt;
    depts[dept].employees.add(r.employee_name);

    const tk = r.trip_id||r.trip_title;
    if (!trips[tk]) trips[tk] = { title:r.trip_title, advance:parseFloat(r.advance_amount||0), spent:0 };
    trips[tk].spent += amt;

    const mo = String(r.expense_date||"").substring(0,7);
    if (mo) byMonth[mo] = (byMonth[mo]||0) + amt;
  });

  return {
    empList:   Object.values(emps).sort((a,b)=>b.total-a.total),
    monthList: Object.entries(byMonth).sort(([a],[b])=>a.localeCompare(b))
                .map(([m,total])=>({ month:m, total, label:MONTHS_LABEL[parseInt(m.substring(5,7))-1] })),
    deptList:  Object.values(depts).sort((a,b)=>b.total-a.total),
    tripList:  Object.values(trips).filter(t=>t.advance>0||t.spent>0),
    totalSpent, cashAmt, cashCnt, billCnt, totalCnt:rows.length,
    catMap:cats, foodSub,
    catSegs: Object.entries(cats).sort(([,a],[,b])=>b-a)
               .map(([label,value],i)=>({ label, value, color:CATEGORY_COLORS[label]||CAT_COLOR_LIST[i%CAT_COLOR_LIST.length] })),
  };
}

// ── SVG: Donut Chart ─────────────────────────────────────────────────────────
function polar(cx, cy, r, deg) {
  const rad = ((deg-90)*Math.PI)/180;
  return { x:cx+r*Math.cos(rad), y:cy+r*Math.sin(rad) };
}
function arcPath(cx, cy, oR, iR, s, e) {
  const delta=Math.min(e-s,359.9999), end=s+delta, large=delta>180?1:0;
  const o1=polar(cx,cy,oR,s), o2=polar(cx,cy,oR,end);
  const i1=polar(cx,cy,iR,end), i2=polar(cx,cy,iR,s);
  return `M${o1.x},${o1.y} A${oR},${oR},0,${large},1,${o2.x},${o2.y} L${i1.x},${i1.y} A${iR},${iR},0,${large},0,${i2.x},${i2.y} Z`;
}
function DonutChart({ segments, size=180, thickness=36 }) {
  const [hov, setHov] = useState(null);
  const total = segments.reduce((s,d)=>s+d.value,0);
  if (!total) return <div style={{ height:size, display:"flex", alignItems:"center", justifyContent:"center", color:"#9ca3af", fontSize:12 }}>No data</div>;
  const cx=size/2, cy=size/2, oR=size/2-6, iR=oR-thickness;
  let cum=0;
  const segs = segments.map(d=>{ const start=cum*360; cum+=d.value/total; const end=cum*360; return {...d,start,end,pct:(d.value/total*100).toFixed(1)}; });
  const h = hov!=null ? segs[hov] : null;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow:"visible", flexShrink:0 }}>
      {segs.map((d,i)=>(
        <path key={i} d={arcPath(cx,cy,hov===i?oR+5:oR,iR,d.start,d.end)}
          fill={d.color} opacity={hov!==null&&hov!==i?0.4:1}
          onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)}
          style={{ cursor:"pointer", transition:"all 0.15s" }}>
          <title>{d.label}: ₹{fmt(d.value)} ({d.pct}%)</title>
        </path>
      ))}
      {/* White center text */}
      <text x={cx} y={cy-7}  textAnchor="middle" fontSize={13} fontWeight="700" fill="#ffffff">
        {h ? fmtShort(h.value) : fmtShort(total)}
      </text>
      <text x={cx} y={cy+10} textAnchor="middle" fontSize={10} fontWeight="700" fill={h ? h.color : "#d1d5db"}>
        {h ? `${h.pct}%` : "Total"}
      </text>
      {h && <text x={cx} y={cy+25} textAnchor="middle" fontSize={9} fill={h.color}>{h.label}</text>}
    </svg>
  );
}

// ── SVG: Line Trend Chart ────────────────────────────────────────────────────
function TrendChart({ data: pts, color="#E31E24" }) {
  const [hovIdx, setHovIdx] = useState(null);
  if (!pts || pts.length === 0) return <div style={{ height:140, display:"flex", alignItems:"center", justifyContent:"center", color:"#9ca3af", fontSize:13 }}>No data</div>;
  const W=540, H=160, PAD={t:16,r:16,b:30,l:52};
  const iW=W-PAD.l-PAD.r, iH=H-PAD.t-PAD.b;
  const maxV = Math.max(...pts.map(d=>d.total), 1);
  const gridY = [0,0.25,0.5,0.75,1].map(f=>({ y:PAD.t+iH-f*iH, label:fmtShort(maxV*f) }));
  const svgPt = (i) => ({
    x: PAD.l + (pts.length===1 ? iW/2 : (i/(pts.length-1))*iW),
    y: PAD.t + iH - (pts[i].total/maxV)*iH,
  });
  const lineD = pts.map((_,i)=>`${i===0?"M":"L"}${svgPt(i).x.toFixed(1)},${svgPt(i).y.toFixed(1)}`).join(" ");
  const areaD = `${lineD} L${svgPt(pts.length-1).x},${PAD.t+iH} L${svgPt(0).x},${PAD.t+iH} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", overflow:"visible" }}>
      <defs>
        <linearGradient id={`mg-${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      {/* Grid lines + Y-axis labels (white) */}
      {gridY.map((g,i)=>(
        <g key={i}>
          <line x1={PAD.l} y1={g.y} x2={W-PAD.r} y2={g.y} stroke="rgba(255,255,255,0.1)" strokeWidth={1}/>
          <text x={PAD.l-6} y={g.y+3.5} textAnchor="end" fontSize={9} fill="#d1d5db">{g.label}</text>
        </g>
      ))}
      {/* Area fill */}
      {pts.length>1 && <path d={areaD} fill={`url(#mg-${color.replace("#","")})`}/>}
      {/* Line */}
      {pts.length>1 && <path d={lineD} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"/>}
      {/* Points + month labels (white) */}
      {pts.map((_,i)=>{
        const p=svgPt(i); const isH=hovIdx===i;
        return (
          <g key={i} onMouseEnter={()=>setHovIdx(i)} onMouseLeave={()=>setHovIdx(null)} style={{cursor:"default"}}>
            <circle cx={p.x} cy={p.y} r={isH?6:4} fill={color} stroke="white" strokeWidth={2} style={{transition:"r 0.15s"}}/>
            {isH && (
              <g>
                <rect x={p.x-36} y={p.y-32} width={72} height={22} rx={4} fill={color}/>
                <text x={p.x} y={p.y-17} textAnchor="middle" fontSize={10} fill="white" fontWeight="700">{fmtShort(pts[i].total)}</text>
              </g>
            )}
            <text x={p.x} y={H-4} textAnchor="middle" fontSize={9} fill="#d1d5db">{pts[i].label}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ── CSV Export Helpers ────────────────────────────────────────────────────────
function buildCsvBlob(titleLine, header, dataRows, totalRow) {
  return { titleLine, header, dataRows, totalRow };
}
function triggerDownload(report, filename) {
  downloadRowsAsXlsx({ filename, title:report.titleLine,
    subtitleRows:[`Generated: ${new Date().toLocaleString("en-IN")}`],
    headers:report.header, rows:report.dataRows, totalRow:report.totalRow });
}
function exportFullCSV(rows, from, to, managerName) {
  if (!rows.length) { alert("No data to export."); return; }
  const header = ["Employee","Email","Department","Trip","Purpose","Destination","Trip Start","Trip End","Trip Status","Expense Date","Category","Food Sub-Category","Description","Bill Type","Amount (₹)","Advance (₹)"];
  const dataRows = rows.map(r=>[
    r.employee_name, r.employee_email, r.department||"",
    r.trip_title, r.trip_purpose||"", r.destination||"",
    r.start_date?new Date(r.start_date).toLocaleDateString("en-IN"):"",
    r.end_date?new Date(r.end_date).toLocaleDateString("en-IN"):"",
    r.trip_status,
    r.expense_date?new Date(r.expense_date).toLocaleDateString("en-IN"):"",
    r.category, r.food_subcategory||"", r.description,
    r.bill_type==="cash_voucher"?"Cash Voucher":r.bill_type==="cash_bill"?"Cash Bill":"Bill",
    parseFloat(r.amount||0).toFixed(2), parseFloat(r.advance_amount||0).toFixed(2),
  ]);
  const grand = rows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  triggerDownload(buildCsvBlob(`TEAM EXPENSE REPORT — ${managerName} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["","","","","","","","","","","","","","GRAND TOTAL",grand.toFixed(2),""]), `Team_Expenses_${from}_to_${to}.csv`);
}
function exportSummaryCSV(rows, from, to, managerName) {
  if (!rows.length) { alert("No data to export."); return; }
  const header = ["Employee","Department","Trips","Expense Items","Total Spent (₹)","Total Advance (₹)","Balance (₹)"];
  const dataRows = rows.map(r=>[r.employee_name, r.department||"", r.trip_count, r.expense_count,
    parseFloat(r.total_spent||0).toFixed(2), parseFloat(r.total_advance||0).toFixed(2),
    (parseFloat(r.total_advance||0)-parseFloat(r.total_spent||0)).toFixed(2)]);
  triggerDownload(buildCsvBlob(`TEAM EXPENSE SUMMARY — ${managerName} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, null), `Team_Summary_${from}_to_${to}.csv`);
}
function exportDeptCSV(rows, from, to, managerName) {
  const deptMap = {};
  rows.forEach(r=>{ const d=r.department||"Other"; if (!deptMap[d]) deptMap[d]={total:0,count:0,emps:new Set()}; deptMap[d].total+=parseFloat(r.amount||0); deptMap[d].count++; deptMap[d].emps.add(r.employee_name); });
  const header = ["Department","Employees","Expense Items","Total Spent (₹)","% of Total"];
  const grand = Object.values(deptMap).reduce((s,v)=>s+v.total,0);
  const dataRows = Object.entries(deptMap).sort(([,a],[,b])=>b.total-a.total).map(([dept,d])=>[dept, d.emps.size, d.count, d.total.toFixed(2), grand>0?(d.total/grand*100).toFixed(1)+"%":"0%"]);
  triggerDownload(buildCsvBlob(`DEPARTMENT BREAKDOWN — ${managerName} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["TOTAL","","",grand.toFixed(2),"100%"]), `Department_Breakdown_${from}_to_${to}.csv`);
}
function exportCategoryCSV(rows, from, to, managerName) {
  const catMap = {}; rows.forEach(r=>{ catMap[r.category]=(catMap[r.category]||0)+parseFloat(r.amount||0); });
  const header = ["Category","Total Spent (₹)","% of Total"];
  const grand = Object.values(catMap).reduce((s,v)=>s+v,0);
  const dataRows = Object.entries(catMap).sort(([,a],[,b])=>b-a).map(([cat,val])=>[cat, val.toFixed(2), grand>0?(val/grand*100).toFixed(1)+"%":"0%"]);
  triggerDownload(buildCsvBlob(`CATEGORY BREAKDOWN — ${managerName} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["TOTAL",grand.toFixed(2),"100%"]), `Category_Breakdown_${from}_to_${to}.csv`);
}
function exportCashCSV(rows, from, to, managerName) {
  const cashRows = rows.filter(r=>r.bill_type==="cash_voucher"||r.bill_type==="cash_bill");
  if (!cashRows.length) { alert("No cash/voucher expenses in this period."); return; }
  const header = ["Employee","Department","Trip","Expense Date","Category","Description","Bill Type","Amount (₹)"];
  const dataRows = cashRows.map(r=>[r.employee_name, r.department||"", r.trip_title, r.expense_date?new Date(r.expense_date).toLocaleDateString("en-IN"):"", r.category, r.description, r.bill_type==="cash_voucher"?"Cash Voucher":"Cash Bill", parseFloat(r.amount||0).toFixed(2)]);
  const grand = cashRows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  triggerDownload(buildCsvBlob(`CASH & VOUCHER EXPENSES — ${managerName} | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["","","","","","","TOTAL",grand.toFixed(2)]), `Cash_Voucher_Expenses_${from}_to_${to}.csv`);
}
function exportEmployeeCSV(rows, from, to, empName) {
  const filtered = rows.filter(r=>r.employee_name===empName);
  if (!filtered.length) { alert(`No expenses for ${empName} in this period.`); return; }
  const header = ["Trip","Trip Status","Expense Date","Category","Food Sub-Category","Description","Bill Type","Amount (₹)"];
  const dataRows = filtered.map(r=>[r.trip_title, r.trip_status, r.expense_date?new Date(r.expense_date).toLocaleDateString("en-IN"):"", r.category, r.food_subcategory||"", r.description, r.bill_type==="cash_voucher"?"Cash Voucher":r.bill_type==="cash_bill"?"Cash Bill":"Bill", parseFloat(r.amount||0).toFixed(2)]);
  const grand = filtered.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  triggerDownload(buildCsvBlob(`${empName} — Expense Detail | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`, header, dataRows, ["","","","","","","TOTAL",grand.toFixed(2)]), `${empName.replace(/\s+/g,"_")}_Expenses_${from}_to_${to}.csv`);
}

function exportEmpByMonthCSV(rows, empName, year, month, cfrom, cto) {
  const range = getRange(year, month, cfrom, cto);
  const filtered = rows.filter(r => {
    if (r.employee_name !== empName) return false;
    const dt = String(r.expense_date || "").substring(0, 10);
    return dt >= range.from && dt <= range.to;
  });
  if (!filtered.length) { alert(`No expenses for ${empName} in the selected period.`); return; }
  const header = ["Expense Date","Trip","Trip Status","Category","Food Sub-Category","Description","Bill Type","Amount (₹)"];
  const dataRows = filtered
    .sort((a, b) => String(a.expense_date).localeCompare(String(b.expense_date)))
    .map(r => [
      r.expense_date ? new Date(r.expense_date).toLocaleDateString("en-IN") : "",
      r.trip_title, r.trip_status||"",
      r.category, r.food_subcategory||"", r.description,
      r.bill_type==="cash_voucher"?"Cash Voucher":r.bill_type==="cash_bill"?"Cash Bill":"Bill",
      parseFloat(r.amount||0).toFixed(2),
    ]);
  const grand = filtered.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  const label  = periodLabel(year, month, cfrom, cto);
  triggerDownload(
    buildCsvBlob(`${empName} — ${label}`, header, dataRows,
      ["","","","","","","TOTAL", grand.toFixed(2)]),
    `${empName.replace(/\s+/g,"_")}_${year}${month?`_M${month}`:""}.csv`
  );
}

function exportEmpTripwiseCSV(rows, empName, from, to) {
  const filtered = rows.filter(r => r.employee_name === empName);
  if (!filtered.length) { alert(`No expenses for ${empName} in this period.`); return; }
  const tripMap = {};
  filtered.forEach(r => {
    const tk = r.trip_title || "Unnamed Trip";
    if (!tripMap[tk]) tripMap[tk] = { title:tk, advance:parseFloat(r.advance_amount||0), status:r.trip_status||"—", rows:[] };
    tripMap[tk].rows.push(r);
  });
  const header = ["Trip / Expense Date","Category","Food Sub-Category","Description","Bill Type","Amount (₹)","Notes"];
  const allDataRows = [];
  Object.values(tripMap).forEach(trip => {
    const tripTotal = trip.rows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
    const overBudget = trip.advance>0 && tripTotal>trip.advance;
    allDataRows.push([
      `TRIP: ${trip.title}  |  Status: ${trip.status}  |  Advance: ₹${trip.advance.toFixed(2)}  |  Spent: ₹${tripTotal.toFixed(2)}`,
      "","","","","", overBudget?"⚠️ OVER BUDGET":""
    ]);
    trip.rows
      .sort((a,b)=>String(a.expense_date).localeCompare(String(b.expense_date)))
      .forEach(r => allDataRows.push([
        r.expense_date ? new Date(r.expense_date).toLocaleDateString("en-IN") : "",
        r.category, r.food_subcategory||"", r.description,
        r.bill_type==="cash_voucher"?"Cash Voucher":r.bill_type==="cash_bill"?"Cash Bill":"Bill",
        parseFloat(r.amount||0).toFixed(2), "",
      ]));
    allDataRows.push(["","","","","Trip Subtotal", tripTotal.toFixed(2),
      overBudget ? `Over by ₹${(tripTotal-trip.advance).toFixed(2)}` : "Within budget"]);
    allDataRows.push([]); // blank separator
  });
  const grand = filtered.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  triggerDownload(
    buildCsvBlob(
      `${empName} — Trip-wise Report | ${new Date(from).toLocaleDateString("en-IN")} to ${new Date(to).toLocaleDateString("en-IN")}`,
      header, allDataRows, ["","","","","GRAND TOTAL", grand.toFixed(2), ""]
    ),
    `${empName.replace(/\s+/g,"_")}_Tripwise_${from}_to_${to}.csv`
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ManagerAnalytics() {
  const { user } = useAuth();
  const accent = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";

  const [tab,       setTab]       = useState("analytics");
  const [year,      setYear]      = useState(String(currentYear));
  const [month,     setMonth]     = useState("");
  const [customFrom,setCustomFrom]= useState("");
  const [customTo,  setCustomTo]  = useState("");
  const [data,      setData]      = useState(null);

  // ── Individual employee report period (separate from main analytics filter) ──
  const [rptYear,      setRptYear]      = useState(String(currentYear));
  const [rptMonth,     setRptMonth]     = useState(String(new Date().getMonth()+1).padStart(2,"0"));
  const [rptCustomFrom,setRptCustomFrom]= useState("");
  const [rptCustomTo,  setRptCustomTo]  = useState("");
  const [sum,       setSum]       = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [drillEmp,  setDrillEmp]  = useState(null);
  const [empFilter, setEmpFilter] = useState("");

  const canFetch = year !== "custom" || (customFrom && customTo);

  const fetchData = useCallback(async (yr, mo, cfrom, cto) => {
    if (yr === "custom" && (!cfrom || !cto)) return;
    setLoading(true); setDrillEmp(null); setEmpFilter("");
    const { from, to } = getRange(yr, mo, cfrom, cto);
    try {
      const [expRes, sumRes] = await Promise.all([
        API.get(`/reports/expenses?from=${from}&to=${to}`),
        API.get(`/reports/summary?from=${from}&to=${to}`),
      ]);
      setData({ rows:expRes.data, from, to });
      setSum(sumRes.data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (year !== "custom") fetchData(year, month, "", "");
  }, [year, month]);

  const empNames    = useMemo(() => !data ? [] : [...new Set(data.rows.map(r=>r.employee_name).filter(Boolean))].sort(), [data]);
  const aggRows     = useMemo(() => !data ? [] : empFilter ? data.rows.filter(r=>r.employee_name===empFilter) : data.rows, [data, empFilter]);
  const agg         = useMemo(() => aggRows.length ? aggregateRows(aggRows) : null, [aggRows]);
  const filteredSum = useMemo(() => !sum ? null : empFilter ? sum.filter(s=>s.employee_name===empFilter) : sum, [sum, empFilter]);

  const totalAdv  = filteredSum ? filteredSum.reduce((s,r)=>s+parseFloat(r.total_advance||0),0) : 0;
  const balance   = totalAdv - (agg?.totalSpent||0);
  const teamSize  = filteredSum ? filteredSum.length : 0;
  const advUtil   = totalAdv>0 && agg ? Math.min(agg.totalSpent/totalAdv*100,999).toFixed(1) : "0.0";
  const cashPct   = agg && agg.totalSpent>0 ? (agg.cashAmt/agg.totalSpent*100).toFixed(1) : "0.0";
  const overspend = agg ? agg.tripList.filter(t=>t.spent>t.advance).length : 0;
  const catSegs   = agg?.catSegs || [];
  const maxEmp    = agg?.empList[0]?.total || 1;
  const maxDept   = agg?.deptList[0]?.total || 1;

  const drillData = drillEmp && agg ? agg.empList.find(e=>e.name===drillEmp) : null;
  const drillSegs = drillData
    ? Object.entries(drillData.categories).sort(([,a],[,b])=>b-a).map(([cat,val],i)=>({ label:cat, value:val, color:CATEGORY_COLORS[cat]||CAT_COLOR_LIST[i%CAT_COLOR_LIST.length] }))
    : [];

  const managerLabel = user?.name || "Manager";
  const from = data?.from, to = data?.to;

  // Input style for dark cards — solid bg so browser option popup is also dark
  const selStyle = {
    padding:"8px 12px", borderRadius:8,
    border:"1.5px solid rgba(255,255,255,0.2)",
    background:"#1a1b2e", color:"#f9fafb",
    fontSize:13, fontFamily:"inherit", colorScheme:"dark",
  };
  const selClass = "ma-dark-sel";
  const labelStyle = { fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5, letterSpacing:"0.05em" };

  // Reusable row/label colors for dark cards
  const txtW  = "#f9fafb";
  const txtM  = "#d1d5db";
  const txtD  = "#9ca3af";

  return (
    <>
      <style>{CSS}</style>
      <div>

        {/* Header */}
        <div style={{ marginBottom:20 }}>
          <h1 className="page-title">📊 Team Analytics & Reports</h1>
          <p className="page-subtitle">Insights and download reports for your team's expenses</p>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:4, marginBottom:20, flexWrap:"wrap" }}>
          {[["analytics","📈 Analytics"],["reports","📥 Download Reports"]].map(([t,l])=>(
            <button key={t} onClick={()=>setTab(t)} style={{
              padding:"9px 20px", borderRadius:8,
              border:`2px solid ${tab===t?accent:"rgba(255,255,255,0.15)"}`,
              background:tab===t?accent:"transparent",
              color:"white", fontWeight:700, fontSize:13, cursor:"pointer", transition:"all 0.2s",
            }}>{l}</button>
          ))}
        </div>

        {/* ── Filters ── */}
        <div className="card" style={{ marginBottom:20 }}>
          <div className="ma-frow">

            {/* Year / Period type */}
            <div>
              <div style={labelStyle}>Year</div>
              <select className={selClass} value={year} onChange={e=>{ setYear(e.target.value); setMonth(""); setEmpFilter(""); }} style={selStyle}>
                <option value="last12">📅 Last 12 Months</option>
                {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                <option value="custom">📆 Custom Range</option>
              </select>
            </div>

            {/* Month — always visible when a specific year is selected */}
            {year !== "last12" && year !== "custom" && (
              <div>
                <div style={labelStyle}>Month</div>
                <select className={selClass} value={month} onChange={e=>setMonth(e.target.value)} style={selStyle}>
                  <option value="">All Months (YTD)</option>
                  {MONTHS_FULL.map((m,i)=><option key={i} value={String(i+1).padStart(2,"0")}>{m}</option>)}
                </select>
              </div>
            )}

            {/* Custom date range */}
            {year === "custom" && (
              <>
                <div>
                  <div style={labelStyle}>From Date</div>
                  <input type="date" className={selClass} value={customFrom} onChange={e=>setCustomFrom(e.target.value)} style={{ ...selStyle, width:148 }}/>
                </div>
                <div>
                  <div style={labelStyle}>To Date</div>
                  <input type="date" className={selClass} value={customTo} onChange={e=>setCustomTo(e.target.value)} style={{ ...selStyle, width:148 }}/>
                </div>
              </>
            )}

            {/* Employee filter — visible once data is loaded */}
            {empNames.length > 0 && (
              <div>
                <div style={labelStyle}>Employee</div>
                <select className={selClass} value={empFilter} onChange={e=>{ setEmpFilter(e.target.value); setDrillEmp(null); }} style={selStyle}>
                  <option value="">👥 All Team Members</option>
                  {empNames.map(n=><option key={n} value={n}>👤 {n}</option>)}
                </select>
              </div>
            )}

            <div style={{ flex:1 }}/>
            <button onClick={()=>fetchData(year, month, customFrom, customTo)}
              disabled={!canFetch}
              style={{ padding:"8px 20px", borderRadius:8, border:`2px solid ${accent}`, background:canFetch?accent:"rgba(255,255,255,0.1)", color:"white", fontWeight:700, fontSize:13, cursor:canFetch?"pointer":"not-allowed", opacity:canFetch?1:0.5 }}>
              🔄 Refresh
            </button>
          </div>

          {/* Period label row */}
          {data && !loading && (
            <div style={{ marginTop:10, fontSize:12, color:txtD, display:"flex", flexWrap:"wrap", gap:10, alignItems:"center" }}>
              <span>📅 <strong style={{ color:txtW }}>{periodLabel(year, month, customFrom, customTo)}</strong>
                {data.from && <span style={{ marginLeft:6 }}>· {new Date(data.from).toLocaleDateString("en-IN")} — {new Date(data.to).toLocaleDateString("en-IN")}</span>}
              </span>
              {empFilter && (
                <span style={{ display:"flex", alignItems:"center", gap:6, padding:"2px 10px", background:"rgba(129,140,248,0.15)", border:"1px solid rgba(129,140,248,0.35)", borderRadius:20, color:"#818cf8", fontWeight:700 }}>
                  👤 {empFilter}
                  <button onClick={()=>setEmpFilter("")} style={{ background:"none", border:"none", color:"#818cf8", cursor:"pointer", fontSize:13, lineHeight:1, padding:0 }}>✕</button>
                </span>
              )}
            </div>
          )}
          {year === "custom" && (!customFrom || !customTo) && (
            <div style={{ marginTop:8, fontSize:12, color:"#fbbf24" }}>⚠️ Select both From and To dates, then click Refresh.</div>
          )}
        </div>

        {loading && (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
            <div style={{ color:txtD }}>Loading team data...</div>
          </div>
        )}

        {!loading && data && (
          <>
            {/* ── KPI Cards ── */}
            <div className="ma-kpi" style={{ marginBottom:20 }}>
              {[
                { label:"Team Members",     value:teamSize,                        icon:"👥", c:"#818cf8", bg:"rgba(129,140,248,0.15)" },
                { label:"Total Spent",      value:fmtShort(agg?.totalSpent||0),    icon:"💸", c:"#f87171", bg:"rgba(248,113,113,0.12)" },
                { label:"Total Advance",    value:fmtShort(totalAdv),              icon:"💰", c:"#fbbf24", bg:"rgba(251,191,36,0.12)"  },
                { label:"Balance",          value:fmtShort(Math.abs(balance)),     icon:balance>=0?"✅":"⚠️", c:balance>=0?"#34d399":"#f87171", bg:balance>=0?"rgba(52,211,153,0.12)":"rgba(248,113,113,0.12)" },
                { label:"Adv. Utilisation", value:`${advUtil}%`,                   icon:"📊", c:"#22d3ee", bg:"rgba(34,211,238,0.12)"  },
                { label:"Cash %",           value:`${cashPct}%`,                   icon:"💵", c:parseFloat(cashPct)>30?"#f87171":"#94a3b8", bg:parseFloat(cashPct)>30?"rgba(248,113,113,0.12)":"rgba(148,163,184,0.1)" },
                { label:"# Expenses",       value:(agg?.totalCnt||0).toLocaleString(), icon:"📋", c:"#22d3ee", bg:"rgba(34,211,238,0.12)" },
                { label:"Overspend Trips",  value:overspend,                       icon:"🚨", c:overspend>0?"#f87171":"#34d399", bg:overspend>0?"rgba(248,113,113,0.12)":"rgba(52,211,153,0.12)" },
              ].map(k=>(
                <div key={k.label} style={{ background:k.bg, borderRadius:12, padding:"14px 16px", border:`1px solid ${k.c}30` }}>
                  <div style={{ fontSize:10, fontWeight:700, color:k.c, opacity:0.85, textTransform:"uppercase", letterSpacing:"0.04em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{k.label}</div>
                  <div style={{ fontSize:17, fontWeight:800, color:k.c, marginTop:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{k.icon} {k.value}</div>
                </div>
              ))}
            </div>

            {/* ══ ANALYTICS TAB ══ */}
            {tab === "analytics" && (
              <>
                {/* Row 1: Category Donut + Monthly Trend */}
                <div className="ma-2col" style={{ marginBottom:16 }}>

                  <div className="card">
                    <h3 style={{ fontWeight:700, fontSize:15, marginBottom:16, color:txtW }}>📊 Category Breakdown</h3>
                    {catSegs.length===0 ? (
                      <div style={{ textAlign:"center", color:txtD, padding:30 }}>No expense data</div>
                    ) : (
                      <div style={{ display:"flex", gap:16, alignItems:"center", flexWrap:"wrap" }}>
                        <DonutChart segments={catSegs} />
                        <div style={{ flex:1, minWidth:120 }}>
                          {catSegs.map(s=>(
                            <div key={s.label} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:9 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                                <div style={{ width:10, height:10, borderRadius:2, background:s.color, flexShrink:0 }}/>
                                <span style={{ fontSize:12, color:txtM }}>{CATEGORY_ICONS[s.label]||"📌"} {s.label}</span>
                              </div>
                              <div style={{ textAlign:"right" }}>
                                <div style={{ fontSize:12, fontWeight:700, color:s.color }}>{fmtShort(s.value)}</div>
                                <div style={{ fontSize:10, color:txtD }}>{agg?.totalSpent>0?(s.value/agg.totalSpent*100).toFixed(1):0}%</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="card">
                    <h3 style={{ fontWeight:700, fontSize:15, marginBottom:16, color:txtW }}>📈 Monthly Spend Trend</h3>
                    {agg?.monthList.length ? (
                      <>
                        <TrendChart data={agg.monthList} color={accent} />
                        <div style={{ marginTop:10, display:"flex", justifyContent:"space-between", fontSize:12, color:txtD }}>
                          <span>📈 Peak: <strong style={{ color:txtW }}>{fmtShort(Math.max(...agg.monthList.map(m=>m.total)))}</strong></span>
                          <span>📉 Avg: <strong style={{ color:txtW }}>{fmtShort(agg.monthList.reduce((s,m)=>s+m.total,0)/agg.monthList.length)}</strong></span>
                        </div>
                      </>
                    ) : <div style={{ textAlign:"center", color:txtD, padding:30 }}>No data</div>}
                  </div>
                </div>

                {/* Row 2: Department + Food Subcategory + Bill Type */}
                <div className="ma-3col" style={{ marginBottom:16 }}>

                  <div className="card">
                    <h3 style={{ fontWeight:700, fontSize:14, marginBottom:14, color:txtW }}>🏢 By Department</h3>
                    {agg?.deptList.length ? agg.deptList.map((d,i)=>(
                      <div key={d.name} style={{ marginBottom:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <span style={{ fontSize:12, fontWeight:600, color:txtM }}>{d.name}</span>
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontSize:12, fontWeight:700, color:CAT_COLOR_LIST[i%CAT_COLOR_LIST.length] }}>{fmtShort(d.total)}</div>
                            <div style={{ fontSize:10, color:txtD }}>{[...d.employees].length} member{[...d.employees].length!==1?"s":""}</div>
                          </div>
                        </div>
                        <div style={{ height:6, background:"rgba(255,255,255,0.08)", borderRadius:4 }}>
                          <div style={{ height:"100%", width:`${(d.total/maxDept)*100}%`, background:CAT_COLOR_LIST[i%CAT_COLOR_LIST.length], borderRadius:4, transition:"width 0.4s" }}/>
                        </div>
                      </div>
                    )) : <div style={{ color:txtD, fontSize:13, padding:"20px 0", textAlign:"center" }}>No data</div>}
                  </div>

                  <div className="card">
                    <h3 style={{ fontWeight:700, fontSize:14, marginBottom:14, color:txtW }}>🍽️ Food Breakdown</h3>
                    {Object.keys(agg?.foodSub||{}).length===0 ? (
                      <div style={{ color:txtD, fontSize:13, padding:"20px 0", textAlign:"center" }}>No food expenses</div>
                    ) : (() => {
                      const grandFood = agg.catMap["Food"]||0;
                      const foodSegs  = [
                        ...FOOD_SUB_ORDER.filter(k=>agg.foodSub[k]>0).map(k=>({ label:k, value:agg.foodSub[k], color:FOOD_SUB_COLORS[k] })),
                        ...((grandFood - FOOD_SUB_ORDER.reduce((s,k)=>s+(agg.foodSub[k]||0),0)) > 0
                          ? [{ label:"Other", value:grandFood-FOOD_SUB_ORDER.reduce((s,k)=>s+(agg.foodSub[k]||0),0), color:"#64748b" }] : []),
                      ];
                      return (
                        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                          <DonutChart segments={foodSegs} size={110} thickness={24} />
                          <div style={{ flex:1 }}>
                            {foodSegs.map(s=>(
                              <div key={s.label} style={{ display:"flex", justifyContent:"space-between", marginBottom:7 }}>
                                <span style={{ fontSize:12, color:txtM }}>{FOOD_SUB_ICONS[s.label]||"🍴"} {s.label}</span>
                                <div style={{ textAlign:"right" }}>
                                  <div style={{ fontSize:12, fontWeight:700, color:s.color }}>{fmtShort(s.value)}</div>
                                  <div style={{ fontSize:10, color:txtD }}>{grandFood>0?(s.value/grandFood*100).toFixed(1):0}%</div>
                                </div>
                              </div>
                            ))}
                            <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid rgba(255,255,255,0.08)", fontSize:11, color:txtD }}>
                              Total Food: <strong style={{ color:txtW }}>{fmtShort(grandFood)}</strong>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="card">
                    <h3 style={{ fontWeight:700, fontSize:14, marginBottom:14, color:txtW }}>🧾 Bill Type</h3>
                    {agg && agg.totalCnt>0 ? (() => {
                      const billAmt  = agg.totalSpent - agg.cashAmt;
                      const billPct  = agg.totalSpent>0 ? billAmt/agg.totalSpent*100 : 0;
                      const cashPctV = agg.totalSpent>0 ? agg.cashAmt/agg.totalSpent*100 : 0;
                      return (
                        <>
                          <div style={{ height:12, borderRadius:6, overflow:"hidden", display:"flex", marginBottom:16 }}>
                            <div style={{ width:`${billPct}%`, background:"#818cf8", transition:"width 0.5s" }}/>
                            <div style={{ width:`${cashPctV}%`, background:"#fbbf24", transition:"width 0.5s" }}/>
                          </div>
                          {[
                            { label:"Bill / Invoice", icon:"🧾", amt:billAmt,     cnt:agg.billCnt, color:"#818cf8", pct:billPct },
                            { label:"Cash / Voucher", icon:"💵", amt:agg.cashAmt, cnt:agg.cashCnt, color:"#fbbf24", pct:cashPctV },
                          ].map(b=>(
                            <div key={b.label} style={{ marginBottom:10, padding:"10px 12px", background:"rgba(255,255,255,0.05)", borderRadius:8 }}>
                              <div style={{ display:"flex", justifyContent:"space-between" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                  <div style={{ width:10, height:10, borderRadius:2, background:b.color, flexShrink:0 }}/>
                                  <span style={{ fontSize:12, fontWeight:600, color:txtM }}>{b.icon} {b.label}</span>
                                </div>
                                <span style={{ fontSize:12, fontWeight:700, color:b.color }}>{fmtShort(b.amt)}</span>
                              </div>
                              <div style={{ fontSize:11, color:txtD, marginTop:3 }}>{b.cnt} entries · {b.pct.toFixed(1)}%</div>
                            </div>
                          ))}
                          {cashPctV > 40 && (
                            <div style={{ padding:"8px 10px", background:"rgba(251,191,36,0.12)", borderRadius:6, fontSize:11, color:"#fbbf24", border:"1px solid rgba(251,191,36,0.3)" }}>
                              ⚠️ High cash ratio — consider requesting more bills.
                            </div>
                          )}
                        </>
                      );
                    })() : <div style={{ color:txtD, fontSize:13, padding:"20px 0", textAlign:"center" }}>No data</div>}
                  </div>
                </div>

                {/* Row 3: Team Member Breakdown */}
                <div className="card" style={{ marginBottom:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                    <h3 style={{ fontWeight:700, fontSize:15, color:txtW }}>👥 Team Member Breakdown</h3>
                    {drillEmp && (
                      <button onClick={()=>setDrillEmp(null)} style={{ fontSize:12, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:6, padding:"4px 12px", cursor:"pointer", color:txtM }}>
                        ← All Members
                      </button>
                    )}
                  </div>

                  {drillEmp && drillData ? (
                    <div>
                      <div style={{ padding:"12px 16px", background:"rgba(129,140,248,0.12)", borderRadius:10, marginBottom:16, display:"flex", alignItems:"center", gap:12, border:"1px solid rgba(129,140,248,0.25)" }}>
                        <div style={{ width:42, height:42, borderRadius:"50%", background:"#818cf8", display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontWeight:800, fontSize:18, flexShrink:0 }}>
                          {drillData.name[0]}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:15, color:txtW }}>{drillData.name}</div>
                          <div style={{ fontSize:12, color:txtD }}>
                            {drillData.dept||"—"} · Total: <strong style={{ color:"#818cf8" }}>₹{fmt(drillData.total)}</strong>
                            {drillData.total>0 && <span style={{ marginLeft:12, color:drillData.cash/drillData.total>0.4?"#f87171":"#34d399" }}>
                              💵 Cash: {(drillData.cash/drillData.total*100).toFixed(1)}%
                            </span>}
                          </div>
                        </div>
                        <button onClick={()=>data && exportEmployeeCSV(data.rows, data.from, data.to, drillData.name)}
                          style={{ padding:"7px 14px", borderRadius:8, border:`1px solid ${accent}`, background:"transparent", color:accent, fontWeight:700, fontSize:12, cursor:"pointer" }}>
                          ⬇️ Download Excel
                        </button>
                      </div>

                      <div className="ma-2col">
                        <div>
                          <div style={{ fontWeight:600, fontSize:13, color:txtM, marginBottom:10 }}>Spend by Category</div>
                          <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                            <DonutChart segments={drillSegs} size={140} thickness={28} />
                            <div style={{ flex:1 }}>
                              {drillSegs.map(s=>(
                                <div key={s.label} style={{ display:"flex", justifyContent:"space-between", marginBottom:7, padding:"5px 8px", background:"rgba(255,255,255,0.04)", borderRadius:6 }}>
                                  <span style={{ fontSize:12, color:txtM }}>{CATEGORY_ICONS[s.label]||"📌"} {s.label}</span>
                                  <div style={{ textAlign:"right" }}>
                                    <div style={{ fontSize:12, fontWeight:700, color:s.color }}>₹{fmt(s.value)}</div>
                                    <div style={{ fontSize:10, color:txtD }}>{(s.value/drillData.total*100).toFixed(1)}%</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          {Object.keys(drillData.foodSub||{}).length>0 && (
                            <div style={{ marginTop:12, padding:"10px 12px", background:"rgba(52,211,153,0.08)", borderRadius:8, border:"1px solid rgba(52,211,153,0.2)" }}>
                              <div style={{ fontSize:12, fontWeight:700, color:"#34d399", marginBottom:6 }}>🍽️ Food Sub-categories</div>
                              {FOOD_SUB_ORDER.filter(k=>drillData.foodSub[k]>0).map(k=>(
                                <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4, color:txtM }}>
                                  <span>{FOOD_SUB_ICONS[k]||"🍴"} {k}</span>
                                  <strong style={{ color:FOOD_SUB_COLORS[k] }}>₹{fmt(drillData.foodSub[k])}</strong>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight:600, fontSize:13, color:txtM, marginBottom:10 }}>Trips ({[...drillData.trips].length})</div>
                          {[...drillData.trips].map(trip=>(
                            <div key={trip} style={{ fontSize:12, padding:"7px 10px", background:"rgba(255,255,255,0.04)", borderRadius:6, marginBottom:5, color:txtM }}>
                              ✈️ {trip}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    agg?.empList.length ? agg.empList.map((emp,i)=>{
                      const pct = (emp.total/maxEmp)*100;
                      const advRow = sum?.find(s=>s.employee_name===emp.name);
                      const adv = parseFloat(advRow?.total_advance||0);
                      const bal = adv - emp.total;
                      return (
                        <div key={emp.name} style={{ marginBottom:12, padding:"12px 14px", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, cursor:"pointer", background:"rgba(255,255,255,0.02)" }}
                          onClick={()=>setDrillEmp(emp.name)}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <div style={{ width:30, height:30, borderRadius:"50%", background:`${accent}22`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, color:accent, flexShrink:0 }}>
                                {i+1}
                              </div>
                              <div>
                                <div style={{ fontWeight:700, fontSize:13, color:txtW }}>{emp.name}</div>
                                <div style={{ fontSize:11, color:txtD }}>{emp.dept||"—"} · {[...emp.trips].length} trip{[...emp.trips].length!==1?"s":""}</div>
                              </div>
                            </div>
                            <div style={{ textAlign:"right" }}>
                              <div style={{ fontWeight:800, fontSize:15, color:accent }}>₹{fmt(emp.total)}</div>
                              {adv>0 && <div style={{ fontSize:11, color:bal>=0?"#34d399":"#f87171" }}>{bal>=0?"✅":"⚠️"} Bal: ₹{fmt(Math.abs(bal))}</div>}
                              {emp.total>0 && <div style={{ fontSize:10, color:txtD }}>💵 Cash: {(emp.cash/emp.total*100).toFixed(0)}%</div>}
                            </div>
                          </div>
                          <div style={{ height:5, background:"rgba(255,255,255,0.08)", borderRadius:3 }}>
                            <div style={{ height:"100%", width:`${pct}%`, background:accent, borderRadius:3, transition:"width 0.4s" }}/>
                          </div>
                          <div style={{ marginTop:6, display:"flex", gap:5, flexWrap:"wrap" }}>
                            {Object.entries(emp.categories).sort(([,a],[,b])=>b-a).slice(0,4).map(([cat,val])=>(
                              <span key={cat} style={{ fontSize:10, padding:"2px 7px", borderRadius:10, background:"rgba(255,255,255,0.07)", color:txtD }}>
                                {CATEGORY_ICONS[cat]||"📌"} {cat}: {fmtShort(val)}
                              </span>
                            ))}
                            <span style={{ fontSize:10, color:txtD, marginLeft:"auto" }}>Click to drill down →</span>
                          </div>
                        </div>
                      );
                    }) : <div style={{ textAlign:"center", color:txtD, padding:30 }}>No expenses in this period</div>
                  )}
                </div>

                {/* Row 4: Overspend Alert */}
                {agg?.tripList.filter(t=>t.spent>t.advance).length>0 && (
                  <div className="card" style={{ border:"1.5px solid rgba(248,113,113,0.4)", background:"rgba(248,113,113,0.07)", marginBottom:16 }}>
                    <h3 style={{ fontWeight:700, fontSize:14, color:"#f87171", marginBottom:12 }}>🚨 Trips Over Budget</h3>
                    {agg.tripList.filter(t=>t.spent>t.advance).map(t=>(
                      <div key={t.title} style={{ display:"flex", justifyContent:"space-between", padding:"8px 12px", background:"rgba(255,255,255,0.03)", borderRadius:8, marginBottom:6, border:"1px solid rgba(248,113,113,0.2)" }}>
                        <span style={{ fontWeight:600, color:txtM, fontSize:13 }}>{t.title}</span>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ color:"#f87171", fontWeight:700, fontSize:13 }}>Over by ₹{fmt(t.spent-t.advance)}</div>
                          <div style={{ fontSize:11, color:txtD }}>Spent ₹{fmtShort(t.spent)} · Advance ₹{fmtShort(t.advance)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ══ REPORTS TAB ══ */}
            {tab === "reports" && (
              <>
                <div style={{ marginBottom:16, padding:"14px 18px", background:"rgba(129,140,248,0.1)", borderRadius:10, fontSize:13, color:"#818cf8", border:"1px solid rgba(129,140,248,0.2)" }}>
                  📥 All reports scoped to your team members only · Period: <strong>{periodLabel(year, month, customFrom, customTo)}</strong>
                  {data?.rows?.length>0 && <span style={{ marginLeft:12, color:txtD }}>({data.rows.length} expense rows)</span>}
                </div>

                <div className="ma-2col" style={{ marginBottom:16 }}>
                  {[
                    { title:"📋 Full Expense Detail",    desc:"Every expense entry — trip, category, food sub-category, bill type",                   action:()=>data&&exportFullCSV(data.rows,from,to,managerLabel),           color:"#818cf8", bg:"rgba(129,140,248,0.1)"  },
                    { title:"👥 Employee-wise Summary",  desc:"One row per team member — trips, expenses, advance and balance",                        action:()=>sum&&data&&exportSummaryCSV(sum,from,to,managerLabel),          color:"#34d399", bg:"rgba(52,211,153,0.1)"   },
                    { title:"📊 Category Breakdown",     desc:"Total spend per category with percentage share of team spend",                          action:()=>data&&exportCategoryCSV(data.rows,from,to,managerLabel),       color:"#fbbf24", bg:"rgba(251,191,36,0.1)"   },
                    { title:"🏢 Department Breakdown",   desc:"Spend aggregated by department with employee count",                                    action:()=>data&&exportDeptCSV(data.rows,from,to,managerLabel),           color:"#a78bfa", bg:"rgba(167,139,250,0.1)"  },
                    { title:"💵 Cash & Voucher Report",  desc:"Cash voucher and cash bill entries only — for cash reconciliation",                     action:()=>data&&exportCashCSV(data.rows,from,to,managerLabel),           color:"#22d3ee", bg:"rgba(34,211,238,0.1)"   },
                  ].map(opt=>(
                    <div key={opt.title} style={{ background:opt.bg, borderRadius:12, border:`1px solid ${opt.color}40`, padding:"16px 18px" }}>
                      <div style={{ fontWeight:700, fontSize:14, color:opt.color, marginBottom:4 }}>{opt.title}</div>
                      <div style={{ fontSize:12, color:txtD, marginBottom:12, minHeight:36 }}>{opt.desc}</div>
                      <button onClick={opt.action}
                        style={{ padding:"8px 0", borderRadius:8, background:opt.color, color:"white", border:"none", fontWeight:700, fontSize:12, cursor:"pointer", width:"100%" }}>
                        ⬇️ Download Excel
                      </button>
                    </div>
                  ))}
                </div>

                {agg?.empList.length>0 && (
                  <div className="card">
                    <h3 style={{ fontWeight:700, fontSize:14, marginBottom:14, color:txtW }}>📁 Individual Employee Reports</h3>

                    {/* ── Per-employee report period filter ── */}
                    <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:10, padding:"14px 16px", marginBottom:14, border:"1px solid rgba(255,255,255,0.1)" }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"#818cf8", marginBottom:10 }}>📅 Select Period for Individual Download</div>
                      <div className="ma-frow" style={{ alignItems:"flex-end", gap:10 }}>
                        <div>
                          <div style={labelStyle}>Year</div>
                          <select value={rptYear} onChange={e=>{ setRptYear(e.target.value); setRptMonth(""); }} style={{ ...selStyle, fontSize:12 }}>
                            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                            <option value="custom">📆 Custom Range</option>
                          </select>
                        </div>
                        {rptYear !== "custom" && (
                          <div>
                            <div style={labelStyle}>Month</div>
                            <select value={rptMonth} onChange={e=>setRptMonth(e.target.value)} style={{ ...selStyle, fontSize:12 }}>
                              <option value="">All Months</option>
                              {MONTHS_FULL.map((m,i)=><option key={i} value={String(i+1).padStart(2,"0")}>{m}</option>)}
                            </select>
                          </div>
                        )}
                        {rptYear === "custom" && (
                          <>
                            <div>
                              <div style={labelStyle}>From</div>
                              <input type="date" value={rptCustomFrom} onChange={e=>setRptCustomFrom(e.target.value)} style={{ ...selStyle, width:140, fontSize:12 }}/>
                            </div>
                            <div>
                              <div style={labelStyle}>To</div>
                              <input type="date" value={rptCustomTo} onChange={e=>setRptCustomTo(e.target.value)} style={{ ...selStyle, width:140, fontSize:12 }}/>
                            </div>
                          </>
                        )}
                        <div style={{ fontSize:11, color:txtD, paddingBottom:2 }}>
                          Selected: <strong style={{ color:txtW }}>{periodLabel(rptYear, rptMonth, rptCustomFrom, rptCustomTo)}</strong>
                        </div>
                      </div>
                    </div>

                    {/* ── Employee list ── */}
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {agg.empList.map((emp,i)=>(
                        <div key={emp.name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:"rgba(255,255,255,0.04)", borderRadius:8, flexWrap:"wrap", gap:8 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10, flex:1, minWidth:0 }}>
                            <div style={{ width:28, height:28, borderRadius:"50%", background:`${accent}22`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:12, color:accent, flexShrink:0 }}>
                              {i+1}
                            </div>
                            <div style={{ minWidth:0 }}>
                              <div style={{ fontWeight:600, fontSize:13, color:txtW, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{emp.name}</div>
                              <div style={{ fontSize:11, color:txtD }}>
                                {emp.dept||"—"} · {[...emp.trips].length} trip{[...emp.trips].length!==1?"s":""} · ₹{fmt(emp.total)}
                                {emp.total>0 && <span style={{ marginLeft:6, color:"#fbbf24" }}>💵 {(emp.cash/emp.total*100).toFixed(0)}% cash</span>}
                              </div>
                            </div>
                          </div>
                          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                            <button
                              disabled={rptYear==="custom"&&(!rptCustomFrom||!rptCustomTo)}
                              onClick={()=>data&&exportEmpByMonthCSV(data.rows, emp.name, rptYear, rptMonth, rptCustomFrom, rptCustomTo)}
                              title={`Monthly CSV — ${periodLabel(rptYear,rptMonth,rptCustomFrom,rptCustomTo)}`}
                              style={{ padding:"6px 10px", borderRadius:6, background:"transparent", border:"1px solid #818cf8", color:"#818cf8", fontWeight:700, fontSize:11, cursor:"pointer", whiteSpace:"nowrap",
                                opacity:(rptYear==="custom"&&(!rptCustomFrom||!rptCustomTo))?0.4:1 }}>
                              📅 Monthly
                            </button>
                            <button
                              onClick={()=>data&&exportEmpTripwiseCSV(data.rows, emp.name, data.from, data.to)}
                              title="Trip-wise CSV — uses main analytics period"
                              style={{ padding:"6px 10px", borderRadius:6, background:"transparent", border:`1px solid ${accent}`, color:accent, fontWeight:700, fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
                              ✈️ Trip-wise
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop:12, padding:"10px 14px", background:"rgba(52,211,153,0.08)", borderRadius:8, fontSize:12, color:"#34d399", border:"1px solid rgba(52,211,153,0.2)" }}>
                      💡 <strong>📅 Monthly</strong> uses the period filter above · <strong>✈️ Trip-wise</strong> uses the main analytics period ({periodLabel(year, month, customFrom, customTo)}) and groups by trip with budget status.
                    </div>
                  </div>
                )}

                {!data?.rows?.length && (
                  <div style={{ textAlign:"center", padding:"40px 20px", color:txtD }}>No expense data for the selected period.</div>
                )}
              </>
            )}
          </>
        )}

        {!loading && !data && (
          <div style={{ textAlign:"center", padding:"60px 20px", color:txtD }}>
            Select a period and click Refresh to load team analytics.
          </div>
        )}
      </div>
    </>
  );
}
